import { DownloadIcon } from 'lucide-react'

import { Button } from '@/components/ui'
import { cn } from '@/utils'

export type DetailTabKey = 'holdings' | 'activity' | 'realized'

export interface DetailTab {
  key: DetailTabKey
  label: string
  count: number
}

interface TabStripProps {
  tabs: DetailTab[]
  active: DetailTabKey
  onChange: (tab: DetailTabKey) => void
  // A short line before Export, such as "No realized gains found on these statements."
  note?: string
  onExport: () => void
}

// Only the active tab's marker, no full-width underline. Export sits at the right on every tab.
export const TabStrip = ({
  tabs,
  active,
  onChange,
  note,
  onExport,
}: TabStripProps) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div className="flex gap-1" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={tab.key === active}
          onClick={() => onChange(tab.key)}
          className={cn(
            'flex cursor-pointer items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium',
            tab.key === active
              ? 'border-blue-6 text-blue-7'
              : 'border-transparent text-blue-5 hover:text-blue-7',
          )}
        >
          {tab.label}
          <span className="rounded-full bg-blue-2 px-1.5 text-xs tabular-nums text-blue-7">
            {tab.count.toLocaleString('en-US')}
          </span>
        </button>
      ))}
    </div>
    <div className="flex items-center gap-4 pb-1">
      {note && <span className="text-xs text-ink-subtle">{note}</span>}
      <Button theme="outline" size="sm" onClick={onExport}>
        <DownloadIcon />
        Export
      </Button>
    </div>
  </div>
)
